// ABOUTME: Lists reusable WhatsApp application flow models for company settings.
// ABOUTME: Separates job and pre-registration templates with their own permitted actions.
import React from "react";
import Page from "Pages/_Layouts/DefaultInternal";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { useTranslation } from "react-i18next";
import { navigate } from "@gatsbyjs/reach-router";
import { useAuthState, useAuthorization } from "_ReactContext/AuthContext";
import {
    Box,
    Button,
    Chip,
    Icon,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@material-ui/core";
import { apiDirectCall } from "customHooks/useApi";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";

const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    fallback;

interface IWhatsappApplicationFlowModelTable {
    t: any;
    models: Compleo.IObject[];
    makeDefault: (modelId: string) => void;
    removeModel: (modelId: string) => void;
    showDefault: boolean;
    emptyMessage: string;
}

export const WhatsappApplicationFlowModelTable = (
    props: IWhatsappApplicationFlowModelTable
) => {
    const { t, models, makeDefault, removeModel, showDefault, emptyMessage } =
        props;

    return (
        <Paper style={{ width: "100%", flex: 1 }}>
            <Table style={{ width: "100%" }}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("name")}</TableCell>
                        {showDefault && <TableCell>{t("default")}</TableCell>}
                        <TableCell>{t("publishedVersion")}</TableCell>
                        <TableCell align="right">{t("actions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {models.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={showDefault ? 4 : 3}
                                align="center"
                                style={{ padding: 32 }}
                            >
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        models.map((model) => (
                            <TableRow key={String(model.modelId || "")}>
                                <TableCell>
                                    {String(model.name || "")}
                                </TableCell>
                                {showDefault && (
                                    <TableCell>
                                        {model.default === true ? (
                                            <Chip
                                                size="small"
                                                color="primary"
                                                label={t("yes")}
                                            />
                                        ) : (
                                            t("no")
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>
                                    {Number(model.publishedVersion || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    <Box display="inline-flex">
                                        <Tooltip title={t("edit")}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    navigate(
                                                        `/whatsappapplicationflowmodel/edit/${model.modelId}`
                                                    )
                                                }
                                            >
                                                <Icon>edit</Icon>
                                            </IconButton>
                                        </Tooltip>
                                        {showDefault && (
                                            <Tooltip title={t("makeDefault")}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={
                                                            model.default ===
                                                                true ||
                                                            Number(
                                                                model.publishedVersion ||
                                                                    0
                                                            ) <= 0
                                                        }
                                                        onClick={() =>
                                                            makeDefault(
                                                                String(
                                                                    model.modelId ||
                                                                        ""
                                                                )
                                                            )
                                                        }
                                                    >
                                                        <Icon>
                                                            check_circle
                                                        </Icon>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t("delete")}>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    removeModel(
                                                        String(
                                                            model.modelId || ""
                                                        )
                                                    )
                                                }
                                            >
                                                <Icon>delete</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
};

const WhatsappApplicationFlowModelList = () => {
    const moduleName = "WHATSAPPAPPLICATIONFLOWMODELLIST";
    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const { company } = useAuthState();
    const { isFeatureAuthorized } = useAuthorization();
    const showMessage = useShowMessage();
    const [jobModels, setJobModels] = React.useState<Compleo.IObject[]>([]);
    const [preRegistrationModels, setPreRegistrationModels] = React.useState<
        Compleo.IObject[]
    >([]);
    const [loadingJobModels, setLoadingJobModels] = React.useState(true);
    const [loadingPreRegistrationModels, setLoadingPreRegistrationModels] =
        React.useState(true);
    const companyId = Number(company.companyId || 0);
    const canManageJobModels =
        isFeatureAuthorized("AddUser") || isFeatureAuthorized("EditUser");
    const canManagePreRegistrationModels = isFeatureAuthorized(
        "ManageWhatsappSettings"
    );

    const notify = (message: string, typeMessage: "success" | "error") =>
        showMessage({
            message,
            typeMessage,
            timeout: 2500,
            snackBarOrign: {
                vertical: "top",
                horizontal: "center"
            }
        });

    const loadModelsByScope = React.useCallback(
        async (scope: "JOB" | "PRE_REGISTRATION") => {
            const loadingSetter =
                scope === "PRE_REGISTRATION"
                    ? setLoadingPreRegistrationModels
                    : setLoadingJobModels;
            const modelsSetter =
                scope === "PRE_REGISTRATION"
                    ? setPreRegistrationModels
                    : setJobModels;
            loadingSetter(true);
            try {
                const response = await apiDirectCall(
                    "/whatsapp/applicationflowmodels",
                    "post",
                    { companyId, scope }
                );
                modelsSetter(getWhatsappApiResponseData(response).models || []);
            } catch (error) {
                notify(getApiErrorMessage(error, t("loadError")), "error");
            } finally {
                loadingSetter(false);
            }
        },
        [companyId, t]
    );

    const loadModels = React.useCallback(async () => {
        const loaders: Promise<void>[] = [];
        if (canManageJobModels) {
            loaders.push(loadModelsByScope("JOB"));
        } else {
            setJobModels([]);
            setLoadingJobModels(false);
        }
        if (canManagePreRegistrationModels) {
            loaders.push(loadModelsByScope("PRE_REGISTRATION"));
        } else {
            setPreRegistrationModels([]);
            setLoadingPreRegistrationModels(false);
        }

        await Promise.all(loaders);
    }, [canManageJobModels, canManagePreRegistrationModels, loadModelsByScope]);

    React.useEffect(() => {
        loadModels();
    }, [loadModels]);

    const makeDefault = async (modelId: string) => {
        try {
            await apiDirectCall(
                "/whatsapp/makedefaultapplicationflowmodel",
                "post",
                { companyId, modelId }
            );
            notify(t("makeDefaultSuccess"), "success");
            loadModels();
        } catch (error) {
            notify(getApiErrorMessage(error, t("makeDefaultError")), "error");
        }
    };

    const removeModel = async (modelId: string) => {
        try {
            await apiDirectCall(
                "/whatsapp/deleteapplicationflowmodel",
                "post",
                { companyId, modelId }
            );
            notify(t("deleteSuccess"), "success");
            loadModels();
        } catch (error) {
            notify(getApiErrorMessage(error, t("deleteError")), "error");
        }
    };

    const jobContent = loadingJobModels ? (
        <Loading />
    ) : (
        <WhatsappApplicationFlowModelTable
            t={t}
            models={jobModels}
            makeDefault={makeDefault}
            removeModel={removeModel}
            showDefault
            emptyMessage={t("emptyModelsMessage")}
        />
    );
    const preRegistrationContent = loadingPreRegistrationModels ? (
        <Loading />
    ) : (
        <WhatsappApplicationFlowModelTable
            t={t}
            models={preRegistrationModels}
            makeDefault={makeDefault}
            removeModel={removeModel}
            showDefault={false}
            emptyMessage={t("emptyPreRegistrationModelsMessage")}
        />
    );

    return (
        <Page
            featureName={t("a_FeatureName")}
            featureMainDescription={t("a_FeatureMainDescription")}
            maxWidth="lg"
            RightButtonComponent={
                <Box display="flex" flexWrap="wrap">
                    {canManageJobModels && (
                        <Button
                            color="primary"
                            variant="contained"
                            startIcon={<Icon>add</Icon>}
                            onClick={() =>
                                navigate("/whatsappapplicationflowmodel/new")
                            }
                            style={{ marginRight: 8, marginBottom: 8 }}
                        >
                            {t("buttonTopRight")}
                        </Button>
                    )}
                    {canManagePreRegistrationModels && (
                        <Button
                            color="primary"
                            variant="contained"
                            startIcon={<Icon>add</Icon>}
                            onClick={() =>
                                navigate(
                                    "/whatsappapplicationflowmodel/new?scope=PRE_REGISTRATION"
                                )
                            }
                            style={{ marginBottom: 8 }}
                        >
                            {t("buttonPreRegistration")}
                        </Button>
                    )}
                </Box>
            }
        >
            {readyTranslation ? (
                <Box width="100%">
                    {canManageJobModels && (
                        <Box mb={3} width="100%">
                            <Typography variant="h6" gutterBottom>
                                {t("jobModelsTitle")}
                            </Typography>
                            {jobContent}
                        </Box>
                    )}
                    {canManagePreRegistrationModels && (
                        <Box width="100%">
                            <Typography variant="h6" gutterBottom>
                                {t("preRegistrationModelsTitle")}
                            </Typography>
                            {preRegistrationContent}
                        </Box>
                    )}
                </Box>
            ) : (
                <Loading />
            )}
        </Page>
    );
};

export default WhatsappApplicationFlowModelList;
